import type { SiteSettingsContent } from '@/sanity/lib/site-settings';

export type CompanyContactRowIcon = 'mail' | 'phone' | 'mapPin' | 'clock';

export type CompanyContactRow = {
  iconKey: CompanyContactRowIcon;
  label: string;
  value: string;
  href: string | null;
};

type MethodId = 'email' | 'phone' | 'address' | 'businessHours';

export function formatCompanyAddressLines(address: SiteSettingsContent['address']): string {
  if (!address) return '';
  const street = [address.streetLine1, address.streetLine2]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(' ');
  const locality = [address.city, address.state, address.zip]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(', ');
  return [street, locality].filter(Boolean).join(' · ');
}

/** Builds ordered contact rows from site settings and a Sanity checkbox-list (e.g. footer / mobile menu). */
export function buildCompanyContactRows(
  company: SiteSettingsContent,
  methodOrder: string[] | null | undefined,
): CompanyContactRow[] {
  const rows: CompanyContactRow[] = [];
  const order = methodOrder?.length
    ? methodOrder
    : (['email', 'phone', 'address', 'businessHours'] satisfies MethodId[]);

  const pushForId = (id: MethodId) => {
    switch (id) {
      case 'email':
        if (company.email.address.trim()) {
          rows.push({
            iconKey: 'mail',
            label: 'Email',
            value: company.email.address,
            href: company.email.link.trim() || null,
          });
        }
        break;
      case 'phone':
        if (company.phone.number.trim()) {
          rows.push({
            iconKey: 'phone',
            label: 'Phone',
            value: company.phone.number,
            href: company.phone.link.trim() || null,
          });
        }
        break;
      case 'address': {
        const addrText = formatCompanyAddressLines(company.address);
        if (addrText) {
          const mapsUrl = company.address?.googleMapsUrl?.trim();
          rows.push({
            iconKey: 'mapPin',
            label: 'Address',
            value: addrText,
            href: mapsUrl || null,
          });
        }
        break;
      }
      case 'businessHours':
        if (company.businessHours.trim()) {
          rows.push({
            iconKey: 'clock',
            label: 'Business hours',
            value: company.businessHours,
            href: null,
          });
        }
        break;
      default:
    }
  };

  const seen = new Set<string>();
  for (const raw of order) {
    if (raw !== 'email' && raw !== 'phone' && raw !== 'address' && raw !== 'businessHours')
      continue;
    if (seen.has(raw)) continue;
    seen.add(raw);
    pushForId(raw);
  }

  return rows;
}
